import PubSubEventEnum from "@shared-enums/communication/pub.sub.event.enum";
import PubSubTopicEnum from "@shared-enums/communication/pub.sub.topic.enum";
import clientId from "@shared/client.id";

export type PubSubCallback = (payload: { data: any; source?: string }) => void;

class SharedPubSub
{
	private static eventSource: EventSource | null = null;
	private static callbacks: Map<string, PubSubCallback[]> = new Map();
	private static currentTopics: string[] = [];

	private static getMercureUrl(): string | null
	{
		const url = import.meta.env.VITE_MERCURE_URL;

		if (! url)
		{
			console.warn("VITE_MERCURE_URL is not configured");
			return null;
		}

		return url;
	}

	/**
	 * Ouvre une connexion EventSource sur les topics donnés.
	 * Un seul EventSource par client.
	 *
	 * SharedPubSub.connect([PubSubTopicEnum.ADMIN])
	 * SharedPubSub.connect([PubSubTopicEnum.PUBLIC, PubSubTopicEnum.USER], userId)
	 * SharedPubSub.connect([PubSubTopicEnum.PUBLIC])
	 */
	public static connect(topics: PubSubTopicEnum[], userId?: string): void
	{
		this.closeConnection();

		const mercureUrl = this.getMercureUrl();

		if (! mercureUrl)
		{
			return;
		}

		const url = new URL(mercureUrl);
		const resolvedTopics: string[] = [];

		for (const topic of topics)
		{
			if (topic === PubSubTopicEnum.USER && userId)
			{
				const resolved = `${ topic }/${ userId }`;
				url.searchParams.append("topic", resolved);
				resolvedTopics.push(resolved);
			}
			else
			{
				url.searchParams.append("topic", topic);
				resolvedTopics.push(topic);
			}
		}

		this.currentTopics = resolvedTopics;

		this.eventSource = new EventSource(url.toString());
		this.eventSource.onmessage = (event) =>
		{
			try
			{
				const { type, data, source, clientId: messageClientId } = JSON.parse(event.data);

				// Ignorer les messages émis par cette même instance
				if (messageClientId && messageClientId === clientId)
				{
					return;
				}

				const callbacks = this.callbacks.get(type) || [];
				callbacks.forEach(cb => cb({ data, source }));
			}
			catch (e)
			{
				console.error("Mercure message parse error", e);
			}
		};
	}

	public static on(event: PubSubEventEnum, callback: PubSubCallback): void
	{
		if (! this.callbacks.has(event))
		{
			this.callbacks.set(event, []);
		}

		const callbacks = this.callbacks.get(event)!;

		if (! callbacks.includes(callback))
		{
			callbacks.push(callback);
		}
	}

	public static off(event: PubSubEventEnum, callback: PubSubCallback): void
	{
		const callbacks = this.callbacks.get(event);

		if (callbacks)
		{
			const index = callbacks.indexOf(callback);

			if (index !== -1)
			{
				callbacks.splice(index, 1);
			}
		}
	}

	private static closeConnection(): void
	{
		this.eventSource?.close();
		this.eventSource = null;
		this.currentTopics = [];
	}

	/**
	 * Ferme la connexion et la rouvre avec de nouveaux topics.
	 * Les callbacks sont conservés (composants montés via usePubSub).
	 */
	public static reconnect(topics: PubSubTopicEnum[], userId?: string): void
	{
		this.closeConnection();
		this.connect(topics, userId);
	}

	/**
	 * Déconnexion complète : ferme la connexion ET efface tous les callbacks.
	 * À utiliser uniquement au logout.
	 */
	public static disconnect(): void
	{
		this.closeConnection();
		this.callbacks.clear();
	}
}

export default SharedPubSub;
